import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AUTH_STORAGE_KEY,
  getAuthCookie,
} from '../../utils/auth-session.util';
import { TokenRefreshService } from '../token-refresh.service';
import { AuthService } from '../auth.service';
import {
  InactivityWarningDialogComponent,
  InactivityWarningDialogResult,
} from './inactivity-warning-dialog.component';
import { authFlowDebug } from '../../utils/auth-flow-debug.util';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly ngZone = inject(NgZone);

  private inactivitySubscription?: Subscription;
  private warningTimerSubscription?: Subscription;
  private dialogRef: MatDialogRef<
    InactivityWarningDialogComponent,
    InactivityWarningDialogResult
  > | null = null;

  private readonly TOTAL_TIMEOUT = environment.inactivityTimeout || 3600000;
  private readonly WARNING_TIMEOUT = environment.warningTimeout || 300000;
  private readonly INITIAL_TIMEOUT = this.TOTAL_TIMEOUT - this.WARNING_TIMEOUT;

  startTracking(): void {
    if (this.inactivitySubscription) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll', { capture: true }),
        fromEvent(document, 'touchstart'),
      ).pipe(startWith(0));

      this.inactivitySubscription = activityEvents$
        .pipe(switchMap(() => timer(this.INITIAL_TIMEOUT)))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.showInactivityWarning();
          });
        });
    });
  }

  stopTracking(): void {
    if (this.inactivitySubscription) {
      this.inactivitySubscription.unsubscribe();
      this.inactivitySubscription = undefined;
    }
    this.stopWarningTimer();
    this.closeDialog();
  }

  private showInactivityWarning(): void {
    if (!this.checkAuthToken() || this.dialogRef) {
      return;
    }

    this.startWarningTimer();

    authFlowDebug.log('inactivity_warning_shown', 'InactivityService.showInactivityWarning', {
      data: {
        initialTimeoutMs: this.INITIAL_TIMEOUT,
        warningTimeoutMs: this.WARNING_TIMEOUT,
      },
      level: 'warn',
    });

    this.dialogRef = this.dialog.open(InactivityWarningDialogComponent, {
      width: '420px',
      maxWidth: '94vw',
      panelClass: 'bh-confirm-panel',
      disableClose: true,
      autoFocus: 'button',
      restoreFocus: true,
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      if (result === 'stay') {
        authFlowDebug.log('inactivity_stay_clicked', 'InactivityService');
        this.resetSession();
      } else if (result === 'logout') {
        authFlowDebug.log('inactivity_logout_clicked', 'InactivityService');
        this.performLogout('user_clicked_logout_on_inactivity_modal');
      }
    });
  }

  private startWarningTimer(): void {
    this.stopWarningTimer();
    this.warningTimerSubscription = timer(this.WARNING_TIMEOUT).subscribe(() => {
      this.performLogout('inactivity_warning_timeout_elapsed');
    });
  }

  private stopWarningTimer(): void {
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
      this.warningTimerSubscription = undefined;
    }
  }

  private closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  private resetSession(): void {
    this.stopWarningTimer();

    this.tokenRefreshService.refreshTokens().subscribe({
      next: () => {
        this.stopTracking();
        this.startTracking();
      },
      error: () => {
        this.performLogout('inactivity_stay_refresh_failed');
      },
    });
  }

  private performLogout(reason: string): void {
    authFlowDebug.log('inactivity_auto_logout', 'InactivityService.performLogout', {
      reason,
      level: 'warn',
    });
    this.stopWarningTimer();
    this.closeDialog();
    this.authService.logout({
      source: 'InactivityService.performLogout',
      reason,
    });
  }

  private checkAuthToken(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return !!(
      localStorage.getItem(AUTH_STORAGE_KEY) ??
      localStorage.getItem('accessToken') ??
      getAuthCookie(AUTH_STORAGE_KEY)
    );
  }
}
